import { useAsyncData } from 'nuxt/app';
import { computed, readonly, toValue, type MaybeRefOrGetter } from 'vue';
import { useApiModel } from '~/composables/useApiModel';
import type { Campaign } from '~/types/Candidates';
import { mapEntityTags, type ApiTagEntity } from '~/utils/mapEntityTags';
import { mapSourceLink, type ApiSourceLike } from '~/utils/mapSourceLink';

interface ApiCampaignCandidate {
  id: number;
  full_name: string;
  slug: string;
}

interface ApiCampaign {
  id: number;
  title: string;
  slug: string;
  status: 'active' | 'inactive';
  type: 'community' | 'party';
  description: string | null;
  sources?: ApiSourceLike[];
  candidates?: ApiCampaignCandidate[];
  parties?: ApiTagEntity[];
}

function mapCampaign(raw: ApiCampaign): Campaign {
  const candidateTags = mapEntityTags(
    'candidate',
    (raw.candidates ?? []).map((candidate) => ({
      id: candidate.id,
      name: candidate.full_name,
      slug: candidate.slug,
    })),
  );

  return {
    id: raw.slug,
    title: raw.title,
    type: raw.type,
    description: raw.description ?? '',
    candidateIds: [],
    sources: (raw.sources ?? []).map(mapSourceLink),
    tags: [...candidateTags, ...mapEntityTags('party', raw.parties)],
  };
}

export function useCampaign(id: MaybeRefOrGetter<string>) {
  // One-shot, like before: both consumers fix the id for the life of the
  // component (the page captures `route.params.id` once; the modal is mounted
  // with `v-if` per open), so the key never has to change.
  const campaignId = toValue(id);

  // `useAsyncData` rather than refs written from a fire-and-forget IIFE.
  // Nuxt does not await a bare async call, so the old version rendered on the
  // server with `campaign: null` — a blank page, a fallback `<title>`, and no
  // data in the payload — and then refetched from scratch after hydration.
  // Awaiting it puts the campaign in the server HTML and transfers it to the
  // client, which is most of what VTW-574 was measuring. It also dedups the
  // page and modal onto one request per campaign.
  const { data, error, status, refresh } = useAsyncData<Campaign | null>(
    `campaign-${campaignId}`,
    async () => {
      const model = useApiModel<ApiCampaign>('campaigns', { autoLoad: false });
      const raw = await model.find(campaignId);

      return raw ? mapCampaign(raw) : null;
    },
    { default: () => null },
  );

  return {
    campaign: readonly(data),
    pending: computed(() => status.value === 'pending'),
    error: readonly(error),
    // A failed load is worth retrying; a missing campaign is not. `find()`
    // maps a 404 to `null`, so only real failures reach `error`.
    refresh,
  };
}
